import type { CoreStoreType } from '../../store/CoreStore';
import { createGameStore } from '../../store/CoreStore';
import { chatActions } from '../../store/slices/chat/slice';
import type { Message } from '../../store/slices/chat/types';
import { subtitlesActions } from '../../store/slices/subtitles/slice';
import { LIVE_SUPPORT_WELCOME_MESSAGE, ParseType, parseChatMessage } from '../../utils/chat';
import { RestartController } from '../restartController';

import type { IChatCommunicationProcessor } from './types';

type StoreAction =
  | ReturnType<typeof chatActions.storeGameMessage>
  | ReturnType<typeof chatActions.storeLiveSupportMessage>
  | ReturnType<typeof chatActions.storeRecentMessage>
  | ReturnType<typeof chatActions.storeLastMessage>
  | ReturnType<typeof chatActions.setLiveSupportReadStatus>
  | ReturnType<typeof chatActions.setIsGameChatBlocked>
  | ReturnType<typeof chatActions.setIsLiveSupportChatBlocked>
  | ReturnType<typeof chatActions.clearLiveSupportMessages>
  | ReturnType<typeof chatActions.setInitialSupportMessage>
  | ReturnType<typeof chatActions.setChatSubscriptionState>
  | ReturnType<typeof chatActions.setShowHideStatusOfMessage>
  | ReturnType<typeof subtitlesActions.setSubtitle>;

export class ChatCommunicationController {
  #ws?: WebSocket;
  #pingInterval?: number;
  #store?: CoreStoreType;
  #processors: Record<string, IChatCommunicationProcessor> = {};

  private url: string | null = null;
  private jSessionId: string | null = null;
  private userId: string | null = null;
  private tableId: string | null = null;
  private casinoId: string | null = null;
  private tableName: string | null = null;
  private messageId: string | null = null;
  private showOtherMessages: boolean = true;
  private subtitlesEnabled: boolean = false;
  private subtitlesLanguage: string = 'en';

  constructor() {
    RestartController.addListener('chat-communication', () => {
      this.restart();
    });
  }

  connect(
    url: string,
    jSessionId: string,
    userId: string,
    tableId: string,
    casinoId: string,
    tableName: string,
    messageId: string,
    showOtherMessages: boolean,
    subtitlesEnabled: boolean,
    subtitlesLanguage: string
  ): void {
    this.#ws = new WebSocket(url);
    this.#ws.addEventListener('message', this.onWebSocketMessage.bind(this));
    this.#ws.addEventListener('error', this.handleWebSocketError.bind(this));
    this.#ws.addEventListener('close', this.handleWebSocketClose.bind(this));

    this.url = url;
    this.jSessionId = jSessionId;
    this.userId = userId;
    this.tableId = tableId;
    this.casinoId = casinoId;
    this.tableName = tableName;
    this.messageId = messageId;
    this.showOtherMessages = showOtherMessages;
    this.subtitlesEnabled = subtitlesEnabled;
    this.subtitlesLanguage = subtitlesLanguage;

    this.#pingInterval = window.setInterval(() => {
      if (this.#ws && this.#ws.readyState === WebSocket.OPEN) {
        const pingMessage = {
          action: 'ping',
          userId: userId,
          time: Date.now(),
          role: 'GAME_USER',
        };

        this.#ws?.send(JSON.stringify(pingMessage));
      } else if (this.#ws && (this.#ws.readyState === WebSocket.CLOSING || this.#ws.readyState === WebSocket.CLOSED)) {
        this.close();
        this.connect(
          url,
          jSessionId,
          userId,
          tableId,
          casinoId,
          tableName,
          messageId,
          showOtherMessages,
          subtitlesEnabled,
          subtitlesLanguage
        );
      }
    }, 10_000);
    const subscribeMessage = {
      content: 'SUBSCRIBE',
      action: 'SUBSCRIBE',
      jSessionId,
      userId,
      tableId,
      casinoId,
      tableName,
      role: 'GAME_USER',
      messageId,
      preference: {
        showOtherMessages: showOtherMessages,
        subEnabled: subtitlesEnabled,
        subLang: subtitlesLanguage,
      },
    };

    // Subscribe to messages from chat server
    this.#ws.addEventListener('open', () => {
      this.#ws?.send(JSON.stringify(subscribeMessage));
    });
  }

  reconnect(): void {
    if (
      this.url &&
      this.jSessionId &&
      this.userId &&
      this.tableId &&
      this.casinoId &&
      this.tableName &&
      this.messageId
    ) {
      this.connect(
        this.url,
        this.jSessionId,
        this.userId,
        this.tableId,
        this.casinoId,
        this.tableName,
        this.messageId,
        this.showOtherMessages,
        this.subtitlesEnabled,
        this.subtitlesLanguage
      );
    }
  }

  restart(): void {
    this.close();
    this.reconnect();
  }

  sendMessage(message: string): void {
    if (this.#ws && this.#ws.readyState === WebSocket.OPEN) {
      this.#ws.send(message);
    } else {
      console.error('WebSocket connection is not open. Message not sent.');
    }
  }

  onWebSocketMessage(msg: MessageEvent): void {
    let message: Message = JSON.parse(msg.data);

    /* parseChatMessage function is used to make showHide variable to showMessages in message object
       can remove this function once BE changes showHide to showMessage */
    message = parseChatMessage(message as ParseType);

    if (message.action === 'SHOW_HIDE_MESSAGE') {
      this.dispatchToStore(chatActions.setShowHideStatusOfMessage(message));
    } else {
      // NOTE: hotfix for SHOW HIDE MESSAGEs, as they are not currently supported by core
      // TODO: Store showhide messages separetelty
      if (message.tagMessage === 'GAME_USER' && message.action !== 'SHOW_HIDE_MESSAGE') {
        this.dispatchToStore(chatActions.storeGameMessage(message));
      }

      if (message.tagMessage === ('SUPPORT' || 'LIVE_SUPPORT') && message.action !== 'SHOW_HIDE_MESSAGE') {
        this.dispatchToStore(chatActions.storeLiveSupportMessage(message));
        this.dispatchToStore(chatActions.setLiveSupportReadStatus(message.role === 'GAME_USER'));
      }

      if (message.action === 'FRESHCHAT_CONVERSATION_RESOLVED') {
        this.dispatchToStore(chatActions.clearLiveSupportMessages());
        this.dispatchToStore(chatActions.setInitialSupportMessage(LIVE_SUPPORT_WELCOME_MESSAGE));
      }

      // Used in MiniChat
      if (message.tagMessage === 'GAME_USER' && message.action !== 'SHOW_HIDE_MESSAGE') {
        this.dispatchToStore(chatActions.storeRecentMessage(message));
      }
      // Used in ChatRibbonConnected
      if (message.action === 'COMMAND' && message.content !== 'ALERT_JOINED') {
        this.dispatchToStore(chatActions.storeLastMessage(message));
      }

      if (message.status === 'SUCCESS' && message.action === 'SUBSCRIBE') {
        this.dispatchToStore(chatActions.setChatSubscriptionState('CHAT_SUBSCRIBED'));
      }

      if (message.action === 'FRESHCHAT_USER_CREATION' && message.status === 'SUCCESS') {
        this.dispatchToStore(chatActions.setChatSubscriptionState('FRESH_CHAT_SUBSCRIBED'));
      }

      // Block chat
      if (
        message.status === 'SUCCESS' &&
        message.user &&
        message.action &&
        ['SUBSCRIBE', 'BLOCK', 'UNBLOCK'].includes(message.action)
      ) {
        this.dispatchToStore(chatActions.setIsGameChatBlocked(!message.user.chatAllowed));
        this.dispatchToStore(chatActions.setIsLiveSupportChatBlocked(!message.user.supportChatAllowed));
      }
    }

    // Subtitles
    if (message.action === 'SUBTITLES') {
      this.dispatchToStore(subtitlesActions.setSubtitle(message));
    }
  }

  setStore(store: ReturnType<typeof createGameStore>): void {
    this.#store = store;
  }

  setProcessor(processor: IChatCommunicationProcessor): void {
    this.#processors[processor.messageName] = processor;
  }

  close(): void {
    console.log('Closing WebSocket connection');
    clearInterval(this.#pingInterval);

    if (this.#ws) {
      this.#ws.removeEventListener('error', this.handleWebSocketError);
      this.#ws.removeEventListener('close', this.handleWebSocketClose);
      this.#ws.close();
      this.#ws = undefined;
    }
  }

  dispatchToStore(action: StoreAction): void {
    if (this.#store) {
      this.#store.dispatch(action);
    }
  }

  handleWebSocketError(e: Event): void {
    console.error('WebSocket error:', e);
  }

  handleWebSocketClose(e: CloseEvent): void {
    console.log('WebSocket closed:', e);
  }
}
